import CertificationsCounters from './components/CertificationsCounters/CertificationsCounters';
import TeamsFilter from 'components/TeamsFilter/TeamsFilter';
import CertificationTable from './components/CertificationsTable/CertificationsTable';
import CertificationsProvider, { CertificationContext } from 'contexts/certifications';
import NotificationBanner from 'components/NotificationBanner/NotificationBanner';
import CertificationsEmptyState from './components/CertificationsEmptyState/CertificationsEmptyState';
import CertificationsRestricted from './components/CertificationsRestricted/CertificationsRestricted';
import CertificationsNoVouchers from './components/CertificationsNoVouchers/CertificationsNoVouchers';
import {
  CertificationsWrapper,
  Container,
  Counters,
  CtaWrapper,
  Header,
  Title,
} from './components/styles';
import { useContext, useMemo, useRef } from 'react';
import { SessionContext } from 'contexts/session';
import { useCapabilities } from 'contexts/capabilities';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isMobile } from 'react-device-detect';
import { Spinner } from 'components/loadings';

const Certifications = (): JSX.Element => {
  return (
    <CertificationsProvider>
      <Container isMobile={isMobile}>
        <CertificationsContent />
      </Container>
    </CertificationsProvider>
  );
};

const CertificationsContent = (): JSX.Element => {
  const {
    actions,
    emptyState,
    fetching,
    fetchingTable,
    fetchingVoucherSummary,
    isFirstFetching,
    filters,
    voucherSummary,
  } = useContext(CertificationContext);
  const { isImpersonated } = useContext(SessionContext);
  const { canViewCertificationInsights } = useCapabilities();
  const voucherAssignmentV1 = useFlags()?.voucherAssignmentV1 ?? false;

  const hasShownContentRef = useRef(false);

  const isFetching = useMemo(
    () => fetching || fetchingTable || fetchingVoucherSummary,
    [fetching, fetchingTable, fetchingVoucherSummary]
  );

  const isLegacyVoucherPath = !voucherAssignmentV1 || !voucherSummary;

  if (!hasShownContentRef.current && isFirstFetching) {
    if (isFetching) return <Spinner active size="50" />;
    if (canViewCertificationInsights && isLegacyVoucherPath && emptyState) {
      return <CertificationsEmptyState />;
    }
  }

  // ensures that the modal does not close and does not show a loading spinner later (once the user has completed one assignment, and the data is being re-fetched)
  hasShownContentRef.current = true;

  if (!canViewCertificationInsights) {
    return (
      <CertificationsWrapper>
        <CertificationsRestricted />
      </CertificationsWrapper>
    );
  }

  const hasNeverPurchasedVouchers =
    voucherAssignmentV1 && voucherSummary?.voucher_balance?.total_purchased === 0;

  if (hasNeverPurchasedVouchers) {
    return (
      <CertificationsWrapper>
        <CertificationsNoVouchers />
      </CertificationsWrapper>
    );
  }

  return (
    <CertificationsWrapper>
      <Header>
        <Title>Certifications</Title>
        <CtaWrapper>
          <TeamsFilter actions={actions} filters={filters} noMobile={true} />
        </CtaWrapper>
      </Header>

      <NotificationBanner
        styles={{ marginTop: '10px', padding: '15px' }}
        impersonated={isImpersonated}
        onlyMobile={true}
        noCloseButton={true}
        infoIcon={true}
        text="To access the full certifications view, including the certifications history, please switch to a larger screen."
      />

      <Counters>
        <CertificationsCounters />
      </Counters>
      <CertificationTable noMobile={true} isFetching={isFetching} />
    </CertificationsWrapper>
  );
};

export default Certifications;
